import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Buildings,
  CaretRight,
  CheckCircle,
  Circle,
  CurrencyDollar,
  Receipt,
  Rows,
  Sparkle,
  SquaresFour,
  Warning,
} from 'phosphor-react';
import { GlassCard, GlassPageBg } from '../../../components/glass';
import { IPrivateRoutes } from '../../../routes/private.routes';
import { usePermittedSubmodules } from '../../../utils/getSubmodulesForPath';
import { formatCurrency } from '../../../utils/FormatFunctions';
import { useAuditoriaInteligente } from '../../../hooks/useAuditoriaInteligente';
import { fmtDataHora, useCooldownAtivo } from '../../../hooks/useCooldownAtivo';
import { AuditoriaInteligenteIcon } from '../../../components/icons/AuditoriaInteligenteIcon';
import { AUDITORIA_BASE, useAuditoriaContext } from './useAuditoriaContext';
import { AUDITORIA_SUBMODULES, SubMeta } from './pages';
import { ICarteiraKpis, ICarteiraRankings, ICarteiraRisco } from './types';
import { num, qtd } from './format';
import { KpiCard } from './components/KpiCard';
import { InfoBloco, FILTRO } from './components/InfoPopover';
import { UI } from './ui';

const STALE = 5 * 60 * 1000;

type Tone = 'pos' | 'neg' | 'warn';
interface Preview {
  value: string;
  label: string;
  tone?: Tone;
}
const TONE: Record<Tone, string> = { pos: UI.pos, neg: UI.neg, warn: UI.warn };

const segOf = (path: string) => path.slice(`${AUDITORIA_BASE}/`.length);

/** 'YYYY-MM-DD' → 'MM/YYYY'. */
const fmtMesAno = (d?: string | null) => (d ? d.slice(0, 7).split('-').reverse().join('/') : '—');

/** Deriva o preview de cada card a partir dos 2 agregados já buscados (em memória). */
function buildPreviews(kpis?: ICarteiraKpis, riscos?: ICarteiraRisco[]): Record<string, Preview> {
  if (!kpis) return {};
  const notas = num(kpis.notas_saida) + num(kpis.notas_entrada) + num(kpis.notas_servico);
  const div = num(kpis.divergencias);
  const score = Math.round(num(kpis.score_medio));
  const emRisco = (riscos ?? []).filter(r => num(r.score) < 60).length;
  return {
    dashboard: {
      value: formatCurrency(num(kpis.faturamento)),
      label: `${qtd(kpis.total_empresas)} empresas`,
    },
    dossie:
      emRisco > 0
        ? { value: qtd(emRisco), label: 'em risco alto', tone: 'warn' }
        : { value: qtd(kpis.total_empresas), label: 'empresas na carteira' },
    fiscal: { value: formatCurrency(num(kpis.impostos)), label: `${qtd(notas)} notas no período` },
    notas: {
      value: qtd(notas),
      label: `${qtd(kpis.notas_saida)} saídas · ${qtd(kpis.notas_entrada)} entradas`,
    },
    'impostos-apurados': {
      value: formatCurrency(num(kpis.impostos)),
      label: 'apurados no período',
    },
    auditorias: {
      value: qtd(div),
      label: `divergências · score ${score}`,
      tone: div > 0 ? 'neg' : 'pos',
    },
    cruzamentos: {
      value: qtd(div),
      label: 'apontamentos por área',
      tone: div > 0 ? 'warn' : undefined,
    },
    pessoal: { value: qtd(kpis.empresas_com_folha), label: 'empresas com folha' },
    societario: { value: qtd(kpis.total_empresas), label: 'empresas' },
  };
}

interface Achado {
  chave: string;
  severidade: 'critico' | 'atencao' | 'info' | 'bom';
  titulo: string;
  detalhe?: string;
}
const SEV: Record<Achado['severidade'], string> = {
  critico: UI.neg,
  atencao: UI.warn,
  info: UI.brand,
  bom: UI.pos,
};

/** Insights determinísticos (client-side) sobre os dados já buscados — sem IA/rede extra. */
function buildInsights(
  kpis?: ICarteiraKpis,
  riscos?: ICarteiraRisco[],
): { resumo: string; achados: Achado[] } | null {
  if (!kpis) return null;
  const div = num(kpis.divergencias);
  const score = Math.round(num(kpis.score_medio));
  const emRisco = (riscos ?? []).filter(r => num(r.score) < 60);
  const achados: Achado[] = [];

  if (div > 0) {
    achados.push({
      chave: 'divergencias',
      severidade: 'critico',
      titulo: `${qtd(div)} divergências na carteira`,
      detalhe: 'Abra o painel de Auditorias para investigar e priorizar.',
    });
  }
  if (emRisco.length > 0) {
    achados.push({
      chave: 'risco',
      severidade: 'atencao',
      titulo: `${qtd(emRisco.length)} empresa(s) com score abaixo de 60`,
      detalhe: emRisco
        .slice(0, 3)
        .map(e => e.nome)
        .join(' · '),
    });
  }
  const pior = riscos?.[0];
  if (pior && num(pior.divergencias) > 0) {
    achados.push({
      chave: 'top-risco',
      severidade: 'atencao',
      titulo: `Maior risco: ${pior.nome}`,
      detalhe: `${qtd(pior.divergencias)} divergências · score ${Math.round(num(pior.score))}.`,
    });
  }
  if (achados.length === 0) {
    achados.push({
      chave: 'ok',
      severidade: 'bom',
      titulo: 'Carteira saudável',
      detalhe: `Score médio ${score} e nenhuma divergência aberta no período.`,
    });
  }
  const resumo = `Score médio ${score} · ${qtd(div)} divergências · ${qtd(kpis.total_empresas)} empresas.`;
  return { resumo, achados };
}

const HubCard = React.memo(
  ({
    sub,
    meta,
    index,
    preview,
    search,
  }: {
    sub: IPrivateRoutes;
    meta: SubMeta;
    index: number;
    preview?: Preview;
    search: string;
  }) => {
    const navigate = useNavigate();
    const Icon = meta.icon;
    const { cor } = meta;
    return (
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: 0.02 * index }}
        whileHover={{ y: -4 }}
        style={{ height: '100%' }}
      >
        <GlassCard
          intensity="strong"
          hoverable
          p={4}
          cursor="pointer"
          h="full"
          position="relative"
          overflow="hidden"
          onClick={() => navigate({ pathname: sub.path, search })}
          role="button"
          transition="box-shadow 0.25s ease"
          _hover={{ boxShadow: `0 16px 38px -12px ${cor}59` }}
        >
          <Box
            position="absolute"
            top="-38px"
            insetEnd="-38px"
            w="130px"
            h="130px"
            borderRadius="full"
            bg={`radial-gradient(circle, ${cor}24, transparent 68%)`}
            pointerEvents="none"
          />
          <Flex direction="column" gap={3} h="full" position="relative">
            <Flex justify="space-between" align="center">
              <Flex
                align="center"
                justify="center"
                w="44px"
                h="44px"
                borderRadius="14px"
                bgGradient={`linear(to-br, ${cor}, ${cor}AF)`}
                color="white"
                boxShadow={`0 10px 22px -7px ${cor}99`}
              >
                <Icon size={23} weight="fill" />
              </Flex>
              <Flex
                align="center"
                justify="center"
                w="26px"
                h="26px"
                borderRadius="full"
                bg={`${cor}14`}
                color={cor}
              >
                <CaretRight size={14} weight="bold" />
              </Flex>
            </Flex>
            <Heading as="h3" fontSize="15px" fontWeight={700} color={UI.ink}>
              {sub.name}
            </Heading>
            <Box mt="auto" pt={3} borderTop="1px solid rgba(15,23,42,0.06)">
              {preview ? (
                <>
                  <Text
                    fontSize="21px"
                    fontWeight={800}
                    color={preview.tone ? TONE[preview.tone] : UI.ink}
                    lineHeight="1.1"
                  >
                    {preview.value}
                  </Text>
                  <Text fontSize="11.5px" color={UI.faint} mt={0.5} noOfLines={1}>
                    {preview.label}
                  </Text>
                </>
              ) : (
                <Text fontSize="13px" color={UI.muted} lineHeight="1.45" noOfLines={2}>
                  {meta.desc}
                </Text>
              )}
            </Box>
          </Flex>
        </GlassCard>
      </motion.div>
    );
  },
);
HubCard.displayName = 'HubCard';

/** Ordem das seções (por eyebrow do submódulo). */
const SECTION_ORDER = [
  'Visão geral',
  'Fiscal',
  'Contábil',
  'Pessoal',
  'Cadastral',
  'Auditoria',
  'Gerencial',
];

const riscoColor = (score: number): string => {
  if (score < 60) return UI.neg;
  if (score < 80) return UI.warn;
  return UI.pos;
};

export function AuditoriaHubPage() {
  const { carteiraParams, somenteAtivas, onToggleAtivas, onOpenEmpresaById } =
    useAuditoriaContext();
  const { search } = useLocation();
  const { getCarteiraKpis, getCarteiraRankings, postCarteiraAtualizar } = useAuditoriaInteligente();
  const [view, setView] = useState<'cards' | 'tabela'>('cards');
  const toast = useToast();
  const qc = useQueryClient();

  // Atualização manual dos agregados da carteira (snapshot no servidor,
  // cooldown de 5h por combinação de filtros — o 429 vem com mensagem pronta).
  const atualizar = useMutation({
    mutationFn: () => postCarteiraAtualizar(carteiraParams),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-carteira-kpis'] });
      qc.invalidateQueries({ queryKey: ['ai-carteira-rankings'] });
      qc.invalidateQueries({ queryKey: ['ai-carteira-distribuicoes'] });
      qc.invalidateQueries({ queryKey: ['ai-carteira-evolucao'] });
    },
    onError: (err: AxiosError<{ detail?: string }>) =>
      toast({
        status: err.response?.status === 429 ? 'warning' : 'error',
        description:
          err.response?.data?.detail ?? 'Não foi possível atualizar os dados da carteira.',
        duration: 6000,
        isClosable: true,
      }),
  });

  const { data: kpis, isLoading: loadingKpis } = useQuery<ICarteiraKpis>({
    queryKey: ['ai-carteira-kpis', carteiraParams],
    queryFn: getCarteiraKpis,
    staleTime: STALE,
    placeholderData: keepPreviousData,
  });
  const { data: rankings, isLoading: loadingRank } = useQuery<ICarteiraRankings>({
    queryKey: ['ai-carteira-rankings', carteiraParams],
    queryFn: getCarteiraRankings,
    staleTime: STALE,
    placeholderData: keepPreviousData,
  });

  // Cooldown de 5h do snapshot da carteira (o gate real é o servidor; aqui só
  // UX — o hook re-renderiza sozinho quando o horário de liberação passa).
  const carteiraBloqueada = useCooldownAtivo(kpis?.atualizacao_liberada_em);

  const submodules = usePermittedSubmodules(AUDITORIA_BASE);
  const metaBySeg = useMemo(() => new Map(AUDITORIA_SUBMODULES.map(s => [s.seg, s])), []);
  const previews = useMemo(() => buildPreviews(kpis, rankings?.maiores_riscos), [kpis, rankings]);
  const insights = useMemo(() => buildInsights(kpis, rankings?.maiores_riscos), [kpis, rankings]);

  // Agrupa submódulos permitidos por seção (eyebrow), preservando a ordem definida.
  const sections = useMemo(() => {
    const byEyebrow = new Map<string, { sub: IPrivateRoutes; meta: SubMeta }[]>();
    submodules.forEach(sub => {
      const meta = metaBySeg.get(segOf(sub.path));
      if (!meta) return;
      const arr = byEyebrow.get(meta.eyebrow) ?? [];
      arr.push({ sub, meta });
      byEyebrow.set(meta.eyebrow, arr);
    });
    const ordered = [...SECTION_ORDER, ...Array.from(byEyebrow.keys())].filter(
      (v, i, a) => a.indexOf(v) === i,
    );
    return ordered
      .filter(e => byEyebrow.has(e))
      .map(eyebrow => ({ eyebrow, items: byEyebrow.get(eyebrow)! }));
  }, [submodules, metaBySeg]);

  const riscos = rankings?.maiores_riscos ?? [];

  return (
    <GlassPageBg>
      <Box maxW="1320px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        {/* Header */}
        <Flex align="center" gap={4} mb={6}>
          <AuditoriaInteligenteIcon size={60} />
          <Box>
            <Heading
              fontSize={{ base: '24px', md: '32px' }}
              fontWeight={800}
              color={UI.ink}
              letterSpacing="-0.02em"
            >
              Auditoria Inteligente
            </Heading>
            <Text fontSize="sm" color={UI.muted}>
              Visão da carteira — investigue qualquer apontamento em até 2 cliques.
            </Text>
          </Box>
        </Flex>

        {/* Insights (heurística sobre o agregado) */}
        {insights && insights.achados.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <GlassCard intensity="strong" p={5} mb={6} position="relative" overflow="hidden">
              <Box
                position="absolute"
                top="-40px"
                insetEnd="-40px"
                w="160px"
                h="160px"
                borderRadius="full"
                bg={`radial-gradient(circle, ${UI.area}2E, transparent 68%)`}
                pointerEvents="none"
              />
              <Flex align="center" gap={2.5} mb={3} position="relative">
                <Flex
                  align="center"
                  justify="center"
                  w="36px"
                  h="36px"
                  borderRadius="12px"
                  bgGradient={`linear(to-br, ${UI.area}, ${UI.area}B0)`}
                  color="white"
                  flexShrink={0}
                >
                  <Sparkle size={19} weight="fill" />
                </Flex>
                <Box minW={0}>
                  <Text
                    fontSize="11px"
                    fontWeight={800}
                    color={UI.area}
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                  >
                    Insights · resumo da carteira
                  </Text>
                  <Text fontSize="sm" color={UI.ink} fontWeight={500} noOfLines={2}>
                    {insights.resumo}
                  </Text>
                </Box>
                <Box textAlign="end" flexShrink={0} ms="auto">
                  {kpis?.gerado_em && (
                    <Text fontSize="11px" color={UI.muted} whiteSpace="nowrap">
                      Atualizado em {fmtDataHora(kpis.gerado_em)}
                    </Text>
                  )}
                  <Button
                    size="xs"
                    variant="ghost"
                    isLoading={atualizar.isPending}
                    isDisabled={carteiraBloqueada}
                    title={
                      carteiraBloqueada
                        ? `Nova atualização disponível às ${fmtDataHora(
                            kpis?.atualizacao_liberada_em,
                          )}`
                        : undefined
                    }
                    onClick={() => atualizar.mutate()}
                  >
                    {carteiraBloqueada
                      ? `Disponível às ${fmtDataHora(kpis?.atualizacao_liberada_em)}`
                      : 'Atualizar'}
                  </Button>
                </Box>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} position="relative">
                {insights.achados.map(a => (
                  <Flex
                    key={a.chave}
                    align="start"
                    gap={2.5}
                    p={3}
                    borderRadius="lg"
                    bg="rgba(255,255,255,0.55)"
                  >
                    <Box
                      w="8px"
                      h="8px"
                      mt="6px"
                      borderRadius="full"
                      bg={SEV[a.severidade]}
                      flexShrink={0}
                    />
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight={700} color={UI.ink}>
                        {a.titulo}
                      </Text>
                      {a.detalhe && (
                        <Text fontSize="xs" color={UI.muted} noOfLines={2}>
                          {a.detalhe}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                ))}
              </SimpleGrid>
            </GlassCard>
          </motion.div>
        )}

        {/* Filtro de escopo da carteira */}
        <Flex justify="flex-end" mb={3}>
          <Button
            size="sm"
            variant="unstyled"
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={3}
            h="34px"
            borderRadius="full"
            border="1px solid"
            borderColor={somenteAtivas ? UI.brand : 'rgba(15,23,42,0.12)'}
            bg={somenteAtivas ? `${UI.brand}14` : UI.card}
            color={somenteAtivas ? UI.brand : UI.muted}
            fontWeight={700}
            fontSize="12.5px"
            onClick={() => onToggleAtivas(!somenteAtivas)}
            title="Considerar apenas clientes ativos (exclui Inativa / ForaDaDominio / ForaDoAcessorias)"
          >
            {somenteAtivas ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}
            Só clientes ativos
          </Button>
        </Flex>

        {/* Tira de KPIs da carteira */}
        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={7}>
          <KpiCard
            label="Empresas"
            value={qtd(kpis?.total_empresas ?? 0)}
            icon={Buildings}
            iconColor={UI.brand}
            iconBg={UI.infoSoft}
            isLoading={loadingKpis}
            info={
              <InfoBloco
                oque="Quantidade de empresas na carteira. Com o filtro de ativos ligado, conta só as marcadas como Ativa."
                dados="tb_empresa (status ativa, exclusão lógica)"
                filtro={FILTRO.carteira}
              />
            }
          />
          <KpiCard
            label="Faturamento"
            value={formatCurrency(num(kpis?.faturamento))}
            icon={CurrencyDollar}
            iconColor={UI.pos}
            iconBg={UI.posSoft}
            isLoading={loadingKpis}
            info={
              <InfoBloco
                oque="Faturamento da carteira na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (saídas − devoluções + serviços)."
                dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
                filtro={FILTRO.carteira}
              />
            }
          />
          <KpiCard
            label="Impostos"
            value={formatCurrency(num(kpis?.impostos))}
            icon={Receipt}
            iconColor={UI.warn}
            iconBg={UI.warnSoft}
            isLoading={loadingKpis}
            info={
              <InfoBloco
                oque="Total de impostos apurados da carteira no período (fonte de apuração, confiável)."
                dados="extract_impostos.valor_total (por competência)"
                filtro={FILTRO.carteira}
              />
            }
          />
          <KpiCard
            label="Divergências"
            value={qtd(kpis?.divergencias ?? 0)}
            sub={kpis ? `score médio ${Math.round(num(kpis.score_medio))}` : undefined}
            icon={Warning}
            iconColor={num(kpis?.divergencias) > 0 ? UI.neg : UI.pos}
            iconBg={num(kpis?.divergencias) > 0 ? UI.negSoft : UI.posSoft}
            isLoading={loadingKpis}
            info={
              <InfoBloco
                oque="Apontamentos com status DIVERGÊNCIA detectados pelo motor de auditoria em toda a carteira. O score médio é a % de conferências OK por empresa."
                dados="Motor de regras (todas_regras) sobre extract_* fiscal/contábil/folha"
                filtro={FILTRO.carteira}
              />
            }
          />
        </SimpleGrid>

        {/* Título submódulos + toggle Cards/Tabela */}
        <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={2}>
          <Text
            fontSize="11px"
            fontWeight={800}
            color={UI.muted}
            textTransform="uppercase"
            letterSpacing="0.1em"
          >
            {view === 'cards' ? 'Submódulos' : 'Empresas por risco'}
          </Text>
          <Flex bg={UI.neutralSoft} borderRadius="10px" p="3px" gap="2px">
            {(
              [
                ['cards', 'Cards', SquaresFour],
                ['tabela', 'Tabela', Rows],
              ] as const
            ).map(([key, lbl, Ico]) => (
              <Button
                key={key}
                size="xs"
                variant="unstyled"
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                px={3}
                h="28px"
                borderRadius="8px"
                fontWeight={700}
                bg={view === key ? UI.card : 'transparent'}
                color={view === key ? UI.brand : UI.muted}
                boxShadow={view === key ? '0 1px 4px rgba(15,23,42,.08)' : 'none'}
                onClick={() => setView(key)}
              >
                <Ico size={14} weight="bold" />
                {lbl}
              </Button>
            ))}
          </Flex>
        </Flex>

        {submodules.length === 0 && (
          <GlassCard intensity="medium" p={10} textAlign="center">
            <Text color={UI.muted}>Nenhum submódulo disponível para o seu perfil.</Text>
          </GlassCard>
        )}
        {submodules.length > 0 && view === 'cards' && (
          // VISÃO CARDS — agrupada por seção
          <Flex direction="column" gap={6}>
            {sections.map(sec => (
              <Box key={sec.eyebrow}>
                <Flex align="center" gap={2.5} mb={3}>
                  <Box w="7px" h="7px" borderRadius="full" bg={sec.items[0].meta.cor} />
                  <Text
                    fontSize="11px"
                    fontWeight={800}
                    color={UI.muted}
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                  >
                    {sec.eyebrow}
                  </Text>
                  <Box flex="1" h="1px" bg="rgba(15,23,42,0.06)" />
                </Flex>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                  {sec.items.map(({ sub, meta }, i) => (
                    <HubCard
                      key={sub.path}
                      sub={sub}
                      meta={meta}
                      index={i}
                      preview={previews[meta.seg]}
                      search={search}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </Flex>
        )}
        {submodules.length > 0 && view === 'tabela' && (
          // VISÃO TABELA — empresas por risco (reusa maiores_riscos, custo zero)
          <GlassCard intensity="medium" p={0} overflow="hidden">
            {riscos.length === 0 && loadingRank && (
              <Box p={8} textAlign="center" color={UI.muted}>
                Carregando empresas…
              </Box>
            )}
            {riscos.length === 0 && !loadingRank && (
              <Box p={8} textAlign="center" color={UI.muted}>
                Nenhuma empresa com apontamentos no período.
              </Box>
            )}
            {riscos.length > 0 && (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Empresa</Th>
                      <Th isNumeric>Score</Th>
                      <Th isNumeric>Divergências</Th>
                      <Th isNumeric>Alertas</Th>
                      <Th>Classificação</Th>
                      <Th>Cliente desde</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {riscos.map(r => {
                      const score = Math.round(num(r.score));
                      return (
                        <Tr
                          key={r.id}
                          cursor="pointer"
                          _hover={{ bg: UI.rowHover }}
                          onClick={() => onOpenEmpresaById(r.id)}
                        >
                          <Td>
                            <Text fontWeight={700} color={UI.ink} noOfLines={1}>
                              {r.nome}
                            </Text>
                            <Text fontSize="11px" color={UI.faint}>
                              ID {r.external_id} · {r.cnpj}
                            </Text>
                          </Td>
                          <Td isNumeric fontWeight={800} color={riscoColor(score)}>
                            {score}
                          </Td>
                          <Td isNumeric color={num(r.divergencias) > 0 ? UI.neg : UI.muted}>
                            {qtd(r.divergencias)}
                          </Td>
                          <Td isNumeric color={num(r.alertas) > 0 ? UI.warn : UI.muted}>
                            {qtd(r.alertas)}
                          </Td>
                          <Td>
                            <Badge
                              style={{
                                background: `${riscoColor(score)}1A`,
                                color: riscoColor(score),
                              }}
                              borderRadius="full"
                              px={2.5}
                              py={0.5}
                              textTransform="none"
                              fontWeight={700}
                            >
                              {r.classificacao}
                            </Badge>
                          </Td>
                          <Td color={UI.faint} fontSize="12px">
                            {fmtMesAno(r.dt_cli_desde)}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </GlassCard>
        )}
      </Box>
    </GlassPageBg>
  );
}
